import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, LayoutGrid, Calendar, X, Check } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface Tag {
  id: string;
  name: string;
  color: string;
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'meeting_instance', label: 'Meetings' },
  { value: 'task', label: 'Tasks' },
  { value: 'note', label: 'Notes' },
] as const;

interface Scope { id: string; name: string; color: string; }

interface ViewListItem {
  id: string;
  title: string;
  tags: Tag[];
  scopes: Scope[];
  tag_count: number;
  entity_types: string;
  created_at: string;
  updated_at: string;
}

export function ViewsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [views, setViews] = useState<ViewListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  async function loadViews() {
    try {
      setViews(await apiGet<ViewListItem[]>('/views'));
    } catch (err) {
      console.error('Failed to load views:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadViews(); }, []);

  async function handleCreate(title: string, tagIds: string[], entityTypes: string[]) {
    try {
      const view = await apiPost<ViewListItem>('/views', {
        title,
        tag_ids: tagIds,
        entity_types: entityTypes.length > 0 ? entityTypes : undefined,
      });
      setShowCreate(false);
      toast.success('View created');
      navigate(`/views/${view.id}`);
    } catch (err) {
      console.error('Failed to create view:', err);
      toast.error('Failed to create view');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this view?')) return;
    try {
      await apiDelete(`/views/${id}`);
      toast.success('View deleted');
      loadViews();
    } catch (err) {
      console.error('Failed to delete view:', err);
      toast.error('Failed to delete view');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Views</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New View
        </button>
      </div>

      {views.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <LayoutGrid size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No views yet</p>
          <p className="text-sm">Create a view to aggregate tagged content from meetings, tasks, and notes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {views.map((view) => (
            <div
              key={view.id}
              className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors cursor-pointer group"
              onClick={() => navigate(`/views/${view.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <LayoutGrid size={16} className="text-accent flex-shrink-0" />
                  <span className="text-sm font-medium text-text-primary truncate">{view.title}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {view.tags.map((tag) => (
                    <span
                      key={`t-${tag.id}`}
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: tag.color + '20', color: tag.color }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {view.scopes?.map((scope) => (
                    <span
                      key={`s-${scope.id}`}
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: scope.color + '20', color: scope.color }}
                    >
                      {scope.name}
                    </span>
                  ))}
                  {view.tags.length === 0 && (!view.scopes || view.scopes.length === 0) && (
                    <span className="text-xs text-text-muted italic">No filters</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(view.updated_at)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(view.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateViewModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
    </div>
  );
}

function CreateViewModal({
  onSave,
  onClose,
}: {
  onSave: (title: string, tagIds: string[], entityTypes: string[]) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<Set<string>>(
    new Set(['meeting_instance', 'task', 'note'])
  );
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    apiGet<Tag[]>('/tags')
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setLoadingTags(false));
  }, []);

  function toggleTag(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  function toggleEntityType(type: string) {
    setSelectedEntityTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Don't allow removing the last type
        if (next.size <= 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New View</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="e.g., Infrastructure Overview"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-2">Entity Types</label>
            <p className="text-xs text-text-muted mb-2">
              Choose which types of content to include in this view.
            </p>
            <div className="flex flex-wrap gap-2">
              {ENTITY_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleEntityType(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    selectedEntityTypes.has(opt.value)
                      ? 'bg-accent/10 border-accent text-accent'
                      : 'border-border text-text-muted hover:text-text-secondary hover:border-border-hover'
                  }`}
                >
                  {opt.label} {selectedEntityTypes.has(opt.value) ? '✓' : ''}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-2">Filter by Tags</label>
            <p className="text-xs text-text-muted mb-2">
              Select tags to aggregate tagged content.
            </p>
            {loadingTags ? (
              <div className="text-xs text-text-muted">Loading tags...</div>
            ) : allTags.length === 0 ? (
              <div className="text-xs text-text-muted">No tags available. Create tags first.</div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                {allTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                      selectedTagIds.has(tag.id)
                        ? 'bg-accent/10 text-text-primary'
                        : 'text-text-secondary hover:bg-bg-hover'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1">{tag.name}</span>
                    {selectedTagIds.has(tag.id) && (
                      <Check size={14} className="text-accent" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => title.trim() && onSave(title.trim(), Array.from(selectedTagIds), Array.from(selectedEntityTypes))}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
